import { useState, useCallback, useMemo, useEffect } from 'react'

import { authenticateAgnosticUser } from 'auth/use-cases/authenticateAgnosticUser'
import type { Shipment } from 'shipments/domain/Shipment'

import { useAbortController } from '../../../ui/hooks/useAbortController'
import { type Chat, mapShipmentToShipperContact } from '../../domain/Chat'
import { formatChatSubject, formatChatSubtitle } from '../../presenters/chatPresenter'
import { getChatList } from '../../use-cases/getChatList'
import { getOrCreateConversation } from '../../use-cases/getOrCreateConversation'
import { getUserContactInfo } from '../../use-cases/getUserContactInfo'

type Me = Awaited<ReturnType<typeof getUserContactInfo>>

export type UseChatReturn = ReturnType<typeof useChat>

export type UseChatProps = { shipment: Shipment }

export function useChat({ shipment }: UseChatProps) {
  const [isLoading, setIsLoading] = useState(true)
  const [conversation, setConversation] = useState<Chat>()
  const [me, setMe] = useState<Me>()
  const shipper = useMemo(() => mapShipmentToShipperContact(shipment), [shipment])
  const subject = formatChatSubject(me?.name, shipment.stops)
  const subtitle = formatChatSubtitle(shipment, conversation?.participants)
  const entityIdentifier = shipment.spotQuote?.bookedRate?.uuid

  if (!entityIdentifier) {
    throw new Error('Could not find a booked rate for the shipment')
  }

  const abortController = useAbortController()

  const initialize = useCallback(async () => {
    setIsLoading(true)

    try {
      await authenticateAgnosticUser({ entityIdentifier })
      const chatUser = await getUserContactInfo(abortController)
      setMe(chatUser)
    } finally {
      setIsLoading(false)
    }
  }, [entityIdentifier, abortController])

  const openConversation = useCallback(async () => {
    const conversationResponse = await getOrCreateConversation({
      carrier: me,
      subject,
      shipper,
      entityIdentifier,
    })

    setConversation(conversationResponse)
  }, [shipper, subject, entityIdentifier, me])

  const onUnreadsChange = useCallback(async () => {
    if (conversation) {
      return
    }

    const response = await getChatList()
    setConversation(response.at(-1))
  }, [conversation])

  useEffect(() => {
    initialize()
  }, [initialize])

  return {
    conversation,
    subject,
    subtitle,
    isLoading,
    me,
    openConversation,
    onUnreadsChange,
  }
}
